/**
 * ConnectionLine Component
 *
 * SVG bezier curve connecting a translation blob to its source paragraph.
 * Uses a smooth cubic bezier curve for aesthetic appeal.
 *
 * Features:
 * - Smooth bezier curve from source to target
 * - Color matched to paragraph's color index
 * - Hover state with thicker stroke and higher opacity
 * - Non-interactive (pointer-events: none)
 */

import React from "react";
import { getColorForIndex } from "./colorPalette.js";

interface ConnectionLineProps {
  /** X position of source paragraph right edge (pixels) */
  startX: number;
  /** Y position of source paragraph center (pixels) */
  startY: number;
  /** X position of translation blob left edge (pixels) */
  endX: number;
  /** Y position of translation blob center (pixels) */
  endY: number;
  /** Color index for this paragraph */
  colorIndex: number;
  /** Whether this line is currently hovered */
  isHovered: boolean;
}

export function ConnectionLine({
  startX,
  startY,
  endX,
  endY,
  colorIndex,
  isHovered,
}: ConnectionLineProps) {
  const colors = getColorForIndex(colorIndex);

  // Calculate control points for smooth bezier curve
  // Control points extend horizontally for a natural S-curve
  const horizontalDistance = endX - startX;
  const controlOffset = Math.min(80, horizontalDistance * 0.4);

  // Build SVG path:
  // M = move to start
  // C = cubic bezier with two control points and end point
  const path = `
    M ${startX} ${startY}
    C ${startX + controlOffset} ${startY},
      ${endX - controlOffset} ${endY},
      ${endX} ${endY}
  `.trim();

  return (
    <svg
      style={{
        position: "absolute",
        top: 0,
        left: 0,
        width: "100%",
        height: "100%",
        pointerEvents: "none",
        zIndex: 99,
        overflow: "visible",
      }}
    >
      <path
        d={path}
        fill="none"
        stroke={colors.border}
        strokeWidth={isHovered ? 3 : 2}
        strokeLinecap="round"
        opacity={isHovered ? 0.9 : 0.5}
        style={{
          transition: "stroke-width 0.2s ease, opacity 0.2s ease",
        }}
      />
    </svg>
  );
}

export default ConnectionLine;
